import { ProgramGfaArea } from '@shared/models/design/activities/activity-type.model';
import { getProjectProgramBarSegments } from '@features/projects/helpers/project-program-bar.helpers';
import {
  useProjectListResultsByScope,
  useResultsPerGFA,
} from '@features/results/use-results.hooks';
import {
  useProjectListMaxResults,
  useProjectListComparisonMaxima,
  useProjectsMaxTarget,
} from '@client/store/project';
import { HorizontalResultBar } from '@features/proposals/ElementListRow/HorizontalResultBar';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import { Box } from '@mui/material';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { Results } from '@shared/models/unit.model';
import React from 'react';
import TargetIndicator from '../../../charts/TargetIndicator';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { getHBarWidthPercents } from '@ui/BarChart/helpers/bar-chart.helpers';

interface ProjectListItemBarChartsProps {
  projectId: string;
  compact?: boolean;
  gfa: number;
  results?: Results;
  baselineResults?: Results;
  programAreas?: ProgramGfaArea[];
  trailingMetrics?: React.ReactNode;
}

const ProjectListItemBarCharts: React.FC<ProjectListItemBarChartsProps> = ({
  projectId,
  compact = false,
  gfa,
  results,
  baselineResults,
  programAreas = [],
  trailingMetrics,
}) => {
  const maxResults = useProjectListMaxResults();
  const maxTarget = useProjectsMaxTarget();
  const comparisonMaxima = useProjectListComparisonMaxima();
  const displayCurrency = useDisplayCurrency();

  const filteredResults = useProjectListResultsByScope(results);

  const resultsPerGFA = useResultsPerGFA(filteredResults, gfa);
  const filteredBaselineResults = useProjectListResultsByScope(baselineResults);
  const baselinePerGFA = useResultsPerGFA(filteredBaselineResults, gfa);
  const co2ePerGfaMax = compact
    ? comparisonMaxima.co2e
    : Math.max(getConversionFactorValue(maxResults, 'co2e'), maxTarget);

  const sekCostPerGfaMax = compact
    ? comparisonMaxima.cost
    : getConversionFactorValue(maxResults, 'sek');

  const co2e = getConversionFactorValue(resultsPerGFA, 'co2e');
  const segments = getProjectProgramBarSegments(programAreas, gfa, co2e);
  const { co2Pct, costPct } = getHBarWidthPercents({
    co2Value: co2e,
    costValue: getConversionFactorValue(resultsPerGFA, 'sek'),
    maxCo2: co2ePerGfaMax,
    maxCost: sekCostPerGfaMax,
  });

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        ...(compact && {
          borderRadius: '0 3px 3px 0',
          overflow: 'hidden',
        }),
      }}
    >
      <HorizontalResultBar
        co2Value={getConversionFactorValue(resultsPerGFA, 'co2e')}
        costValue={getConversionFactorValue(resultsPerGFA, 'sek')}
        color={compact ? '#A8A7A5' : BASELINE_COLOR}
        baselineCo2Value={
          compact ? getConversionFactorValue(baselinePerGFA, 'co2e') : undefined
        }
        baselineColor={compact ? '#D1D0CD' : undefined}
        rightCornerRadius={3}
        outlineSegments={!compact}
        dimOtherSegmentsOnHover={false}
        maxCo2={co2ePerGfaMax}
        maxCost={sekCostPerGfaMax}
        segments={compact ? undefined : segments}
        hideCost={displayCurrency.isDisabled}
        barHeight={16}
        costBarHeight={3}
      />
      {!compact && (
        <TargetIndicator maxValue={co2ePerGfaMax} projectId={projectId} />
      )}
      {!compact && trailingMetrics && (
        <Box
          sx={{
            position: 'absolute',
            left: `calc(${Math.max(co2Pct, displayCurrency.isDisabled ? 0 : costPct)}% + 12px)`,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            whiteSpace: 'nowrap',
          }}
        >
          {trailingMetrics}
        </Box>
      )}
    </Box>
  );
};

export default ProjectListItemBarCharts;
